// @flow

import { isFinite } from 'lodash';

import { AUSTIN_EVENTS } from 'Common/cast/CastUtils';
import { CastReceiver, ChromeCastReceiver } from 'Browser/cast/receiver';

import { type AustinEvent, AustinError, type AustinProgramInfoRequest } from 'Common/cast/types';
import { styles as playerStyles } from './Player';

/* AUSTIN:
  - DOCUMENTATION: https://hollywood.slingbox.com/austinjs-3.0.24/doc/classes/_austin_client_.austin.html
  - TEST PAGE: https://hollywood.slingbox.com/austinjs-3.0.24/app/player.html

$FlowFixMe - direct import (instead of conditional "require") breaks webpack build) */
// const Austin = process.env.IN_CHROMECAST_MODE ? require('austinjs').default : {};
const Austin = process.env.IN_CHROMECAST_MODE ? require('Common/player/assets/austin.debug').default : {};

export const styles = {
  container: (playingOnCastDevice: boolean) => () => ({
    ...playerStyles.container,
    '> .html5-player .bmpui-seekbar-label-inner': {
      borderBottom: '2px solid #e41932 !important',
    },
    '> .html5-player .bmpui-seekbar-label-inner:after': {
      borderTopColor: '#e41932 !important',
    },
    '> .html5-player .bmpui-seekbar-playbackposition': {
      backgroundColor: '#e41932 !important',
    },
    '> .html5-player .bmpui-ui-selectbox': {
      color: '#e41932 !important',
      fontSize: '1em !important',
    },
    '> .aspect-16x9:before': {
      paddingBottom: '0px !important',
    },
    ...(playingOnCastDevice
      ? {
          /* For player ui in Chromecast mode (hides fullscreen and pip icons) */
          '> .html5-player .bmpui-ui-piptogglebutton': { display: 'none ' },
          '> .html5-player .bmpui-ui-fullscreentogglebutton': { display: 'none ' },
          '> .html5-player .bmpui-ui-watermark': {
            ...(process.env.IN_CHROMECAST_MODE ? { margin: 0, right: 0, top: 0 } : {}),
            backgroundImage: `url(${require('../components/navigation/img/dish_anywhere_logo.png')}) !important`,
            backgroundSize: '66px 43px',
            opacity: 1,
            transition: 'opacity .3s ease-in-out',
            pointerEvents: 'none',
          },
        }
      : {}),
  }),
};

type AustinPlayConfig =
  | {| contentType: 'live', inputType: 'channel_id' | 'service_id', inputId: number |}
  | {| contentType: 'dvr', dvrId: number, dvrProgress: number |};

/* eslint-disable-next-line camelcase */
class ChromeCastSlingPlayer {
  austinHasBeenBootstrapped: boolean = false;
  bitmovinLicense: string;
  onAustinStateChange: AustinEvent => void;
  eventTypeEventListenerPairs: Array<[string, (event: AustinEvent) => void]> = [];
  subscribedToAustinEvents: boolean = false;
  onDisconnect: () => void;
  passEventToStateMachine: string => void;

  constructor(
    bitmovinLicense: string,
    onAustinStateChange: AustinEvent => void,
    onDisconnect: () => void,
    passEventToStateMachine: string => void,
  ) {
    this.bitmovinLicense = bitmovinLicense;
    this.onAustinStateChange = onAustinStateChange;
    this.onDisconnect = onDisconnect;
    this.passEventToStateMachine = passEventToStateMachine;

    if (process.env.IN_DEBUG) {
      window.slingPlayer = this;
      window.austin = Austin;
    }
  }

  requestPause = () => {
    Austin.pause();
  };

  requestResume = () => {
    Austin.resume();
  };

  playStream = (
    finderId: string,
    slingAdmin: string,
    playConfig: AustinPlayConfig,
    receiverId: string,
    uuid: string,
  ) =>
    this.bootstrapAustin()
      .then(() => this.connect(finderId, slingAdmin, receiverId, uuid))
      .then(() => this.play(playConfig))
      .then(austinPlayer => {
        CastReceiver.log(
          `[CAST][AUSTIN] Received instance of austinPlayer from Austin: `,
          austinPlayer,
        );
        return austinPlayer;
      })
      .catch((error: Error | AustinError) => {
        if (error instanceof AustinError) {
          this.handleAustinEvent({ type: 'onError', payload: error });
        } else {
          console.error('[CAST][AUSTIN] Unexpected error has occurred: ', error.message, error);

          const austinError = new AustinError('unknown', error.message);
          this.handleAustinEvent({ type: 'onError', payload: austinError });
        }

        return null; /* null means that playback has failed and no player has been created */
      });

  bootstrapAustin = () => {
    if (this.austinHasBeenBootstrapped) return Promise.resolve();

    const bootstrapParams = {
      env: 'prod',
      log: { level: 'warn' },
      modules: [{ name: 'bitmovin', module: window.bitmovin }],
      platform: 'chromecast',
      protocol: 'https:',
      endpoint: window.location.href,
      prefetchLicence: true,
      customPipeline: true,
      corsProxy: process.env.IN_DEBUG ? window.location.host : null,
    };

    CastReceiver.log(`[CAST][AUSTIN] Attempt to bootstrap: `, bootstrapParams);
    return Austin.bootstrap(bootstrapParams)
      .then(() => {
        this.austinHasBeenBootstrapped = true;

        CastReceiver.log(`[CAST][AUSTIN] Austin has been initialized`);
      })
      .catch(err => {
        console.warn(`[CAST][AUSTIN] Error during Austin initialization:`, err);
        throw new AustinError(
          'fail_to_bootstrap',
          `Fail to bootstrap Austin. Reason: ${JSON.stringify(err)}`,
        );
      });
  };

  connect = async (finderId: string, slingAdmin: string, receiverId: string, uuid: string) => {
    if (Austin.isConnected()) {
      const { finderId: connectedTo } = Austin.getBoxDetails();
      if (connectedTo === finderId) {
        return Promise.resolve();
      }

      /* Fully destroy current instance to prevent issues with media element. */
      await this.destroy();
    }

    this.subscribeToEventsIfNotSubscribed();

    const connectParams = {
      finderId,
      pass: slingAdmin,
      uuid,
      receiverId,
      user: 'admin',
    };

    CastReceiver.log(`[CAST][AUSTIN] Attempt to connect: `, connectParams);

    return Austin.connect(connectParams)
      .then(() => CastReceiver.log(`[CAST][AUSTIN] Connected to Austin`))
      .catch(err => {
        if (err.reason === 'alreadyInConnecting') {
          /* There is no way to check that Austin is in 'connecting' state
             so it's fine to just ignore the error. */
          return undefined;
        }

        console.warn(`[CAST][AUSTIN] Fail to connect to Austin: `, err);

        throw new AustinError(
          'fail_to_connect',
          `Fail to connect to Austin. Reason: ${JSON.stringify(err)}`,
        );
      });
  };

  play = (config: AustinPlayConfig) => {
    const playConfig = this._generateAustinPlayConfig(config);
    CastReceiver.log(`[CAST][AUSTIN] Attempt to play `, playConfig);

    return Austin.play(playConfig)
      .then(() => {
        CastReceiver.log(`[CAST][AUSTIN] Started playing `, playConfig);
        this.disableInfoLogs();
        return Austin.getAustinPlayer();
      })
      .catch(err => {
        console.warn(`[CAST][AUSTIN] Fail to play `, playConfig);

        throw new AustinError(
          'fail_to_play_stream',
          `Fail to play stream. Reason: ${JSON.stringify(err)}`,
        );
      });
  };

  _generateAustinPlayConfig = (partialConfig: AustinPlayConfig) => {
    const baseConfig = {
      playerContainer: CastReceiver.getCastVideoElement().parentElement,
      videoElement: CastReceiver.getCastVideoElement(),
      bitmovinLicence: this.bitmovinLicense,
      defaultUi: false /* prevents rendering of default bitmovin UI */,
      takeOver: true /* always takes over stream */,
    };

    if (partialConfig.contentType === 'dvr') {
      const { dvrId, dvrProgress, contentType } = partialConfig;
      // For now always resume DVRs. Ideally we could ask resume/start over on the client
      // when casting without active DVR playback.
      const inProgress = dvrProgress > 0;

      return {
        ...baseConfig,
        contentType,
        dvr: { resuming: inProgress, programId: dvrId, dvrDetails: { inProgress } },
      };
    }

    const { inputType, inputId, contentType } = partialConfig;
    return { ...baseConfig, contentType, inputType, inputId: inputId.toString() };
  };

  disconnect = () =>
    Austin.isConnected()
      ? Austin.disconnect()
          .then(() => CastReceiver.log(`[CAST][AUSTIN] Disconnected from Austin.`))
          .catch(err => console.warn(`[CAST][AUSTIN] Fail to disconnect: `, err))
          .then(this.onDisconnect)
      : Promise.resolve();

  stopStreaming = () =>
    Austin.isStreaming()
      ? Austin.stopStreaming()
          .then(() => CastReceiver.log('[CAST][AUSTIN] Stopped streaming. '))
          .catch(e => console.warn('[CAST][AUSTIN] Fail to stop streaming: ', e))
      : Promise.resolve();

  destroy = async () => {
    await this.disconnect()
      .then(CastReceiver.resetMediaElement)
      .then(this.unSubscribeFromAustinEvents)
      .then(() => {
        CastReceiver.log('[CAST][AUSTIN] Successfully destroyed Austin instance.');
        // this.austinHasBeenBootstrapped = false;
      });
  };

  /* Austin ignores log level config via bootstrap config for some reason */
  disableInfoLogs = () => Austin.setLogLevel('warn');

  subscribeToEventsIfNotSubscribed = () => {
    if (this.subscribedToAustinEvents === false) {
      CastReceiver.log(`[CAST][AUSTIN] Subscribing to Austin events: ${AUSTIN_EVENTS.join(',')} `);
      AUSTIN_EVENTS.forEach(event => {
        // $FlowFixMe
        const handler = (payload: any) => this.handleAustinEvent({ type: event, payload });
        this.eventTypeEventListenerPairs.push([event, handler]);
        Austin.subscribe(event, handler);
      });
      this.subscribedToAustinEvents = true;
    }
  };

  unSubscribeFromAustinEvents = () => {
    CastReceiver.log(
      `[CAST][AUSTIN] Unsubscribing from Austin events: ${AUSTIN_EVENTS.join(',')} `,
    );

    this.eventTypeEventListenerPairs.forEach(([event, handler]) =>
      Austin.unSubscribe(event, handler),
    );
    this.eventTypeEventListenerPairs = [];
    this.subscribedToAustinEvents = false;

    return Promise.resolve();
  };

  handleAustinEvent = (event: AustinEvent) => {
    if (event.type === 'onError') {
      const { payload } = event;

      if (payload instanceof AustinError) {
        console.error('[CAST][AUSTIN][ERROR]', payload.type, payload.message);
      } else {
        const { info, reason } = payload;
        console.error('[CAST][AUSTIN][ERROR]', reason, info);
      }
    } else if (event.type === 'onPopUp' && event.payload.type === 'generic') {
      let response;
      if (event.payload.message.includes('Your recording has ended')) {
        // This is targeted at a popup asking to switch to live when recording has ended.
        response = {
          buttonInfo: {
            text: 'Live TV',
            url: 'LIVE_TV/rest/v1/receiverid/:_receiverid?correlationid=:_correlationid',
            payload: 'LIVE_TV&creator=0',
          },
        };
      } else {
        // This is targeted at a popup asking to go back to live when switching
        // between live events. Happens when the current live event is not at Live position.
        response = {
          buttonInfo: {
            text: 'Yes',
            url: 'YES/rest/v1/receiverid/:_receiverid?correlationid=:_correlationid',
            payload: 'Yes&creator=0',
          },
        };
      }
      Austin.popUpUserAction(response);
    } else {
      CastReceiver.log(`[CAST][AUSTIN][EVENT] ${event.type}:`, event);
    }
    this.onAustinStateChange(event);
  };

  sendDvrInfoToSenders = () => {
    const duration = Austin.getDuration() / 1000;
    const currentTime = Austin.getCurrentTime() / 1000;

    if (isFinite(duration) && isFinite(currentTime)) {
      const msg = { type: 'AUSTIN_DVR_INFO', payload: { duration, currentTime } };
      const onSend = (senderId: string) =>
        CastReceiver.log(
          `[CAST][AUSTIN] Sending DVR info: currentTime - ${currentTime}, duration - ${duration}, to - ${senderId}`,
        );

      ChromeCastReceiver.sendMessageToAllSenders(msg, onSend);
    }
  };

  isFinishedPlaying = () => Austin.getDuration() === Austin.getCurrentTime();

  seekTo = (absoluteOffsetInS: number) => {
    const currentTimeInS = Austin.getCurrentTime() / 1000;
    const relativeOffset = absoluteOffsetInS - currentTimeInS;

    this.seek(relativeOffset);
  };

  seek = (relativeOffset: number) => {
    const currentTimeInS = Austin.getCurrentTime() / 1000;
    const durationInS = Austin.getDuration() / 1000;

    if (
      (relativeOffset < 0 && currentTimeInS === 0) || // Seeking back, when at position 0
      (relativeOffset > 0 && currentTimeInS >= durationInS) // Seeking forward when at position == duration
    ) {
      console.warn(
        `[CAST][AUSTIN] Not seeking. Current time = (${currentTimeInS}). Duration = ${durationInS}. Relative offset = ${relativeOffset}.`,
      );
      return;
    }

    this.passEventToStateMachine('austinSeek');
    Austin.seek(relativeOffset)
      .then(() =>
        CastReceiver.log(
          `[CAST][AUSTIN] Seeked:
  relative offset - ${relativeOffset},
  currentTime - ${currentTimeInS},
  duration - ${durationInS}`,
        ),
      )
      .catch(error =>
        console.warn(`[CAST][AUSTIN] Fail to seek ${relativeOffset}, reason: `, error),
      );
  };

  getProgramInfo = (): Promise<AustinProgramInfoRequest> =>
    Austin.isStreaming()
      ? Austin.getProgramInfo()
          .then(([info]) => ({ result: 'ok', info }))
          .catch(({ reason }) => ({ result: 'error', reason }))
      : Promise.resolve({ result: 'error', reason: 'not_streaming' });
}

export { ChromeCastSlingPlayer }; // eslint-disable-line camelcase
